import { createContext, useContext, useEffect, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { queryClient, apiRequest } from "./queryClient";
import {
  identifyUser, resetUser,
  trackEvent, registerSuperProperties,
} from "./analytics";

type AuthUser = {
  id: string;
  email: string;
  fullName: string;
  workspaceId: string;
  role: string;
  firstLoginPending?: boolean;
  supportMode?: { workspaceId: string; workspaceName: string };
};

type AuthContextType = {
  user: AuthUser | null;
  isLoading: boolean;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { data: user, isLoading } = useQuery<AuthUser | null>({
    queryKey: ["/api/auth/me"],
    queryFn: async () => {
      try {
        const res = await fetch("/api/auth/me", { credentials: "include" });
        if (res.status === 401) return null;
        if (!res.ok) throw new Error(`Auth check failed: ${res.status}`);
        return res.json();
      } catch (err) {
        if (err instanceof Error && err.message.startsWith("Auth check failed")) throw err;
        throw new Error("Network error during auth check");
      }
    },
    staleTime: 30_000,
    refetchInterval: 30_000,
    retry: 1,
    retryDelay: 2000,
  });

  useEffect(() => {
    if (user) {
      identifyUser(user, "core");

      const lastActiveKey = `octa-last-active-date-${user.workspaceId}`;
      const lastActive = localStorage.getItem(lastActiveKey);
      const now = Date.now();

      if (lastActive) {
        const elapsed = now - parseInt(lastActive, 10);
        const ONE_DAY = 24 * 60 * 60 * 1000;
        const SEVEN_DAYS = 7 * ONE_DAY;

        if (elapsed > SEVEN_DAYS) {
          trackEvent("Dormant User Returned", {
            days_since_last_visit: Math.floor(elapsed / ONE_DAY),
          });
        } else if (elapsed > ONE_DAY) {
          trackEvent("Returning User", {
            days_since_last_visit: Math.floor(elapsed / ONE_DAY),
          });
        }
      }

      try { localStorage.setItem(lastActiveKey, String(now)); } catch {}
      registerSuperProperties({ last_active_date: new Date().toISOString() });

    } else if (!isLoading && user === null) {
      resetUser();
    }
  }, [user, isLoading]);

  useEffect(() => {
    if (!isLoading && user === null && window.location.pathname !== "/") {
      window.location.replace("/");
    }
  }, [user, isLoading]);

  useEffect(() => {
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        queryClient.setQueryData(["/api/auth/me"], null);
        queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  const logout = async () => {
    resetUser();
    const res = await apiRequest("POST", "/api/auth/logout");
    const data = await res.json();
    queryClient.setQueryData(["/api/auth/me"], null);
    queryClient.clear();
    try {
      localStorage.removeItem("octa-active-entity");
    } catch {} /* storage cleanup — intentional */
    try {
      const channel = new BroadcastChannel("octa-entity-sync");
      channel.postMessage({ type: "auth-change" });
      channel.close();
    } catch {}
    if (data.logoutUrl) {
      window.location.replace(data.logoutUrl);
    } else {
      window.location.replace("/");
    }
  };

  return (
    <AuthContext.Provider value={{ user: user ?? null, isLoading, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
